(() => {
  if (customElements.get('course-map')) return;

  customElements.define('course-map', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-course-map-form]');
      this.input = this.querySelector('[data-course-map-input]');
      this.submitButton = this.querySelector('[data-course-map-submit]');
      this.results = this.querySelector('[data-course-map-results]');
      this.status = this.querySelector('[data-course-map-status]');
      this.mapFrame = this.querySelector('[data-course-map-frame]');
      this.mapLoading = this.querySelector('[data-course-map-loading]');
      this.mapLink = this.querySelector('[data-course-map-link]');
      this.selected = this.querySelector('[data-course-map-selected]');
      this.selectedName = this.querySelector('[data-course-map-selected-name]');
      this.selectedLocation = this.querySelector('[data-course-map-selected-location]');
      this.endpoint = this.dataset.endpoint;
      this.minimumLength = Number(this.dataset.minimumLength || 4);
      this.controller = null;
      this.debounceTimer = null;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.search();
      });

      this.input.addEventListener('input', () => {
        window.clearTimeout(this.debounceTimer);
        if (this.input.value.trim().length < this.minimumLength) {
          this.clearResults();
          this.setStatus('');
          return;
        }
        this.debounceTimer = window.setTimeout(() => this.search(), 350);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.clearResults();
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.clearResults();
      };
      document.addEventListener('click', this.onDocumentClick);

      this.mapFrame.addEventListener('load', () => {
        this.mapLoading.hidden = true;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      this.controller?.abort();
      window.clearTimeout(this.debounceTimer);
    }

    setStatus(message) {
      this.status.textContent = message || '';
    }

    clearResults() {
      this.results.replaceChildren();
      this.results.hidden = true;
    }

    renderResults(items) {
      this.clearResults();
      if (!Array.isArray(items) || items.length === 0) {
        this.setStatus(this.dataset.emptyMessage);
        return;
      }

      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        const result = document.createElement('li');
        const button = document.createElement('button');
        const name = document.createElement('span');
        const location = document.createElement('span');

        button.type = 'button';
        button.className = 'course-map__result';
        name.className = 'course-map__result-name';
        location.className = 'course-map__result-location';
        name.textContent = item.name || '';
        location.textContent = [item.city, item.state].filter(Boolean).join(', ');
        button.append(name, location);
        button.addEventListener('click', () => this.selectCourse(item));
        result.appendChild(button);
        fragment.appendChild(result);
      });

      this.results.appendChild(fragment);
      this.results.hidden = false;
      const label = items.length === 1 ? this.dataset.resultSingular : this.dataset.resultPlural;
      this.setStatus(`${items.length} ${label}`);
    }

    selectCourse(item) {
      const location = [item.city, item.state].filter(Boolean).join(', ');
      const query = [item.name, location].filter(Boolean).join(', ');
      const encodedQuery = encodeURIComponent(query);

      this.mapLoading.hidden = false;
      this.mapFrame.src = `https://www.google.com/maps?q=${encodedQuery}&output=embed`;
      this.mapFrame.title = query;
      this.mapLink.href = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
      this.selectedName.textContent = item.name || '';
      this.selectedLocation.textContent = location;
      this.selected.hidden = false;
      this.clearResults();
      this.setStatus(this.dataset.selectedMessage.replace('[course]', item.name || ''));
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < this.minimumLength) {
        this.clearResults();
        this.setStatus(this.dataset.minimumMessage.replace('[count]', this.minimumLength));
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      this.submitButton.disabled = true;
      this.setStatus(this.dataset.loadingMessage);

      try {
        const url = new URL(this.endpoint);
        url.searchParams.set('course_name', query);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Course search returned ${response.status}`);
        this.renderResults(await response.json());
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.clearResults();
          this.setStatus(this.dataset.errorMessage);
        }
      } finally {
        this.submitButton.disabled = false;
      }
    }
  });
})();
